import { useCallback, useEffect, useState } from "react";
import { Button, Card, Empty, Select, Spin, Upload, message } from "antd";
import { UploadOutlined, UserAddOutlined } from "@ant-design/icons";
import {
  fetchPoaSignatureAssets,
  uploadPoaSignatureAsset,
} from "../../../service/settings";

/**
 * Admin settings tab for the attorney signature images.
 *
 * Each signing attorney gets one stored signature image. It is stamped onto
 * generated appeal forms for the appointed POA, and attorneys drag it onto
 * PDFs with "Add attorney signature". The four partners always have a
 * space; any other attorney gets one when an administrator picks them from
 * the staff list and uploads their signature (Matthew, 2026-10-05).
 */
const PoaSignatures = () => {
  const [assets, setAssets] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [newAttorney, setNewAttorney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchPoaSignatureAssets();
      setAssets(res?.data?.assets || []);
      setCandidates(res?.data?.candidates || []);
    } catch (err) {
      console.error("Failed to load POA signature assets:", err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const handleUpload = async (attorneyName, file) => {
    const contentType = (file?.type || "").toLowerCase();
    if (contentType !== "image/png" && contentType !== "image/jpeg") {
      message.error("Signature image must be a PNG or JPEG file.");
      return false;
    }
    setUploadingFor(attorneyName);
    try {
      const formData = new FormData();
      formData.append("attorney_name", attorneyName);
      formData.append("file", file);
      await uploadPoaSignatureAsset(formData);
      message.success(`Signature image saved for ${attorneyName}.`);
      setNewAttorney(null);
      await loadAssets();
    } catch (err) {
      message.error(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          "Couldn't save the signature image — try again."
      );
    } finally {
      setUploadingFor(null);
    }
    return false;
  };

  if (loading && assets.length === 0) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (loadError && assets.length === 0) {
    return (
      <Empty
        description="Couldn't load the attorney signatures. Refresh the page or try again later."
        style={{ padding: 48 }}
      />
    );
  }

  return (
    <div data-testid="poa-signatures-settings" style={{ maxWidth: 720 }}>
      <p style={{ color: "#6b7280", marginBottom: 16 }}>
        Attorneys drag these signatures onto PDFs with &ldquo;Add attorney
        signature&rdquo;, and the appointed POA&apos;s is stamped onto generated
        appeal forms. Upload a clear signature on a transparent background
        (PNG) so it doesn&apos;t cover the signature line. Replacing an image
        affects future signing only.
      </p>
      {assets.map((asset) => (
        <Card
          key={asset.attorney_name}
          size="small"
          style={{ marginBottom: 12 }}
          data-testid={`poa-signature-card-${asset.attorney_name}`}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <div style={{ minWidth: 180 }}>
              <strong>{asset.attorney_name}</strong>
              <div style={{ color: "#6b7280", fontSize: 12 }}>
                {asset.has_signature_image
                  ? `Updated ${
                      asset.updated_at
                        ? new Date(asset.updated_at).toLocaleDateString()
                        : ""
                    }`
                  : "No signature image uploaded yet"}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                minHeight: 48,
                alignItems: "center",
              }}
            >
              {asset.preview_url ? (
                <img
                  src={asset.preview_url}
                  alt={`${asset.attorney_name} signature`}
                  style={{
                    maxHeight: 60,
                    maxWidth: 240,
                    objectFit: "contain",
                    border: "1px solid #e5e7eb",
                    borderRadius: 4,
                    padding: 4,
                    background: "#fff",
                  }}
                />
              ) : (
                <span style={{ color: "#9ca3af", fontSize: 12 }}>
                  No preview
                </span>
              )}
            </div>
            <Upload
              accept="image/png,image/jpeg"
              showUploadList={false}
              beforeUpload={(file) => handleUpload(asset.attorney_name, file)}
              disabled={Boolean(uploadingFor)}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploadingFor === asset.attorney_name}
              >
                {asset.has_signature_image ? "Replace" : "Upload"}
              </Button>
            </Upload>
          </div>
        </Card>
      ))}
      <Card
        size="small"
        style={{ marginBottom: 12, borderStyle: "dashed" }}
        data-testid="poa-signature-add-attorney"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ minWidth: 180 }}>
            <strong>
              <UserAddOutlined style={{ marginRight: 8 }} />
              Add an attorney
            </strong>
            <div style={{ color: "#6b7280", fontSize: 12 }}>
              Pick from the staff list, then upload their signature.
            </div>
          </div>
          <Select
            showSearch
            allowClear
            placeholder="Choose an attorney"
            value={newAttorney}
            onChange={setNewAttorney}
            optionFilterProp="label"
            style={{ flex: 1 }}
            disabled={Boolean(uploadingFor)}
            options={candidates.map((c) => ({
              value: c.attorney_name,
              label: c.email
                ? `${c.attorney_name} (${c.email})`
                : c.attorney_name,
            }))}
            data-testid="poa-signature-add-attorney-select"
          />
          <Upload
            accept="image/png,image/jpeg"
            showUploadList={false}
            beforeUpload={(file) => handleUpload(newAttorney, file)}
            disabled={!newAttorney || Boolean(uploadingFor)}
          >
            <Button
              type="primary"
              icon={<UploadOutlined />}
              disabled={!newAttorney}
              loading={Boolean(newAttorney) && uploadingFor === newAttorney}
            >
              Upload
            </Button>
          </Upload>
        </div>
      </Card>
    </div>
  );
};

export default PoaSignatures;
